<script setup lang="ts">
import type { BeltColor } from '@@/bits/belt_colors'
import {
  getBadgeBackgroundProps,
  getBeltTextColorHex,
  getBeltYearArcColorHex,
  getUntranslatedBeltText,
} from '@@/bits/belt_colors'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSettingsBeltProgressEffectStore } from '@@/pinia/settings_belt_progress_effect'
import { useSmoothAnimation } from '@@/vuecomposables/use_smooth_animation'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { storeToRefs } from 'pinia'
import { computed, CSSProperties, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'

interface Props {
  color?: BeltColor
  year?: number
  xShadow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  color: 'white',
  year: new Date().getFullYear(),
  xShadow: true,
})

const badgeBackgroundProps = computed(() => getBadgeBackgroundProps(props.color))

// Don't translate the texts in badge as it may be very long in some languages
const TEXTS = computed(() => ['Padlet', props.year.toString(), 'Certified', `${getUntranslatedBeltText(props.color)}`])

const TEXT_STYLES: CSSProperties[] = [
  // PADLET
  { 'text-transform': 'uppercase', 'font-family': 'Outfit', 'font-size': '12px', 'font-weight': 900 },
  // YEAR
  {
    'text-transform': 'uppercase',
    'font-family': 'Outfit',
    'font-size': '8px',
    'font-weight': 800,
    'letter-spacing': '2%',
  },
  // BELT COLOR
  {
    'text-transform': 'uppercase',
    'font-family': 'Outfit',
    'font-size': '8px',
    'font-weight': 900,
  },
  // CERTIFIED
  {
    'text-transform': 'uppercase',
    'font-family': 'Outfit',
    'font-size': '8px',
    'font-weight': 500,
  },
]
const TEXT_SPACINGS = [1.04, 1.12, 1.12, 1.12]
const SPACING_BETWEEN_SEGMENTS = [5, 87, 1]
const X_CRANE_LOGO = true
const IS_FLIPPING_CHARS_FOR_TEXTS = [false, false, true, true]

const texts = computed(() => {
  const original = TEXTS.value
  // Find runs of consecutive true in IS_FLIPPING_CHARS_FOR_TEXTS and reverse those subsegments
  const result = [...original]
  let i = 0
  while (i < IS_FLIPPING_CHARS_FOR_TEXTS.length) {
    if (IS_FLIPPING_CHARS_FOR_TEXTS[i]) {
      // Start of a run
      const start = i
      while (i < IS_FLIPPING_CHARS_FOR_TEXTS.length && IS_FLIPPING_CHARS_FOR_TEXTS[i]) {
        i++
      }
      const end = i // exclusive
      // Reverse the subsegment [start, end)
      const sub = result.slice(start, end).reverse()
      for (let j = start; j < end; j++) {
        result[j] = sub[j - start]
      }
    } else {
      i++
    }
  }
  return result
})
const textStyles = computed<CSSProperties[]>(() => TEXT_STYLES)
const textSpacings = computed(() => TEXT_SPACINGS)
const spacingBetweenSegments = computed(() => SPACING_BETWEEN_SEGMENTS)
const xCraneLogo = computed(() => X_CRANE_LOGO)
const isFlippingCharsForTexts = computed(() => IS_FLIPPING_CHARS_FOR_TEXTS)

const charsForTexts = computed(() =>
  texts.value.map((text, index) => {
    const chars = text.split('')
    if (isFlippingCharsForTexts.value[index]) {
      return chars.reverse()
    }
    return chars
  }),
)

// These refs will hold the dynamically calculated styles.
const ringContainerStyle = ref<Record<string, string>>({})
const charStyles = ref<Array<Record<string, string>>>([])
const yearArcPath = ref<string>('')
const yearArcVisible = ref<boolean>(false)

// Use shared effect store for mouse tracking
const effectStore = useSettingsBeltProgressEffectStore()
const { isHovering, globalMouseX, globalMouseY } = storeToRefs(effectStore)

const containerRef = ref<HTMLElement | null>(null)

// Check if user prefers reduced motion for accessibility
const { isInReducedMotionMode } = useReducedMotionMode()

// #region Holographic effect

// Constants for badge dimensions and effect calculations
const BADGE_WIDTH = 112 // Badge width in pixels
const BADGE_HEIGHT = 112 // Badge height in pixels
const CENTER_PERCENTAGE = 50 // Center point in percentage coordinates (50% = center)

// Holographic effect constants
const MAX_CONIC_ANGLE = 36 // Maximum rotation angle for conic gradient (36deg creates subtle rainbow effect that doesn't overwhelm)
const MIN_HOLO_OPACITY = 0.3 // Minimum opacity for holographic overlay when mouse is far from center
const MAX_HOLO_OPACITY = 1.0 // Maximum opacity for holographic overlay when mouse is at center

// Animation constants
const ANIMATION_SNAP_THRESHOLD = 0.01 // Snap to target when difference is below this threshold (prevents infinite animation)
const ANIMATION_EASING_FACTOR = 0.15 // Easing factor for smooth interpolation (0.15 = smooth, 0.3 = faster, 0.05 = slower)

// Cache the bounding rect to avoid expensive getBoundingClientRect() calls on every mouse move
// Cache is computed on first mousemove (when hover starts) and invalidated on mouseleave
const cachedRect = ref<DOMRect | null>(null)

// Watch for hover state changes to manage cache lifecycle
watch(isHovering, (hovering) => {
  if (hovering) {
    // Cache rect on first mousemove when hover starts
    if (containerRef.value && !cachedRect.value) {
      cachedRect.value = containerRef.value.getBoundingClientRect()
    }
  } else {
    // Invalidate cache when mouse leaves
    cachedRect.value = null
  }
})

// Track page scroll to update cached bounding rect
const isPageScrolling = ref(false)

let ticking = false
const onScroll = () => {
  if (!ticking) {
    window.requestAnimationFrame(() => {
      isPageScrolling.value = !isPageScrolling.value // Toggle to trigger reactivity
      ticking = false
    })
    ticking = true
  }
}
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

// Everytime scroll ends
watch(isPageScrolling, (value, oldValue) => {
  if (oldValue === true && value === false) {
    if (containerRef.value) {
      cachedRect.value = containerRef.value.getBoundingClientRect()
    }
  }
})

// Calculate gradient position for CSS (percentage) relative to badge container
// Uses cached rect for performance optimization
const gradientXPercent = computed(() => {
  if (!cachedRect.value || !isHovering.value) return CENTER_PERCENTAGE
  const relativeX = globalMouseX.value - cachedRect.value.left
  const x = (relativeX / BADGE_WIDTH) * 100
  // Clamp to valid range [0, 100]
  return Math.max(0, Math.min(100, x))
})

const gradientYPercent = computed(() => {
  if (!cachedRect.value || !isHovering.value) return CENTER_PERCENTAGE
  const relativeY = globalMouseY.value - cachedRect.value.top
  const y = (relativeY / BADGE_HEIGHT) * 100
  // Clamp to valid range [0, 100]
  return Math.max(0, Math.min(100, y))
})

// Calculate angle for conic gradient based on global mouse position
// Maps angle to create symmetric rotation: 0° → MAX_CONIC_ANGLE → 0° as cursor moves around
const targetConicAngle = computed(() => {
  if (!cachedRect.value) return 0
  if (!isHovering.value) {
    // When not hovering, return 0 and let the smooth animation transition naturally
    return 0
  }

  // Calculate badge center position in global coordinates using cached rect
  const centerX = cachedRect.value.left + cachedRect.value.width / 2
  const centerY = cachedRect.value.top + cachedRect.value.height / 2

  // Calculate angle from global mouse position relative to badge center
  const dx = globalMouseX.value - centerX
  const dy = globalMouseY.value - centerY
  let angle = Math.atan2(dy, dx) * (180 / Math.PI) // Result: -180 to 180

  // Normalize angle to 0-360 range
  angle = (angle + 360) % 360
  let sweep = angle

  // Map 0..180..360 → 0..MAX_CONIC_ANGLE..0 for symmetric effect
  // This creates a peak effect at 180° (left side) and tapers off toward 0°/360° (right side)
  if (sweep <= 180) {
    sweep = (sweep / 180) * MAX_CONIC_ANGLE // First half: 0° → MAX_CONIC_ANGLE
  } else {
    sweep = ((360 - sweep) / 180) * MAX_CONIC_ANGLE // Second half: MAX_CONIC_ANGLE → 0°
  }

  return sweep
})

// Smoothly interpolated angle for display using requestAnimationFrame
// Uses shared animation composable to handle easing, cleanup, and frame management
const conicAngle = useSmoothAnimation(targetConicAngle, {
  snapThreshold: ANIMATION_SNAP_THRESHOLD,
  easingFactor: ANIMATION_EASING_FACTOR,
})

/**
 * Calculate distance-based opacity for holographic effect
 * Opacity fades as mouse moves away from badge center using quadratic falloff
 * - At center: MAX_HOLO_OPACITY (1.0) for strong effect
 * - At edge: MIN_HOLO_OPACITY (0.3) for subtle ambient shine
 * - Respects reduced motion preference for accessibility
 */
const holoOpacity = computed(() => {
  // Disable holographic effect if user prefers reduced motion
  if (isInReducedMotionMode.value) {
    return MIN_HOLO_OPACITY // Keep subtle ambient glow only
  }

  if (!containerRef.value) return 0
  if (!isHovering.value) return MIN_HOLO_OPACITY

  // Calculate Euclidean distance from mouse to badge center (in percentage coordinates)
  const dx = gradientXPercent.value - CENTER_PERCENTAGE
  const dy = gradientYPercent.value - CENTER_PERCENTAGE
  const distance = Math.sqrt(dx * dx + dy * dy)

  // Maximum possible distance from center to corner in percentage space
  // sqrt(50² + 50²) ≈ 70.7%
  const maxDistance = Math.sqrt(CENTER_PERCENTAGE ** 2 + CENTER_PERCENTAGE ** 2)
  const normalizedDistance = Math.min(distance / maxDistance, 1) // Clamp to [0, 1]

  // Apply quadratic falloff curve for smooth, natural-looking fade
  // Formula: minOpacity + range * (1 - distance²)
  // This creates stronger effect near center that gradually fades toward edges
  const opacityRange = MAX_HOLO_OPACITY - MIN_HOLO_OPACITY
  const opacity = MIN_HOLO_OPACITY + opacityRange * (1 - normalizedDistance ** 2)

  return Math.max(MIN_HOLO_OPACITY, Math.min(MAX_HOLO_OPACITY, opacity))
})

//#endregion

// Helper to calculate the starting index for each text segment in the flat charStyles array.
const charsIndexOffsets = computed(() => {
  const offsets = [0]
  for (let i = 0; i < charsForTexts.value.length - 1; i++) offsets.push(offsets[i] + charsForTexts.value[i].length)

  return offsets
})

// watchEffect will re-run automatically whenever its dependencies (like props) change.
watchEffect(async () => {
  // We must wait for the browser to load the fonts before we can measure them.
  await document.fonts.ready

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const radius = 46
  // Start with an offset if the crane logo is present.
  const INITIAL_OFFSET = xCraneLogo.value ? 10 : 0
  let cumulativeAngle = INITIAL_OFFSET

  const styles: Array<Record<string, string>> = []
  let yearStartAngle = 0
  let yearEndAngle = 0

  texts.value.forEach((text, textIndex) => {
    const isFlipping = isFlippingCharsForTexts.value[textIndex]
    const style = textStyles.value[textIndex]
    const textSpacing = textSpacings.value[textIndex] || 1

    // Set canvas font to match the character's style for accurate measurement.
    ctx.font = `${style['font-weight'] || 'normal'} ${style['font-size']} ${style['font-family']}`

    // Track the start angle for the year segment (textIndex === 1)
    if (textIndex === 1) {
      yearStartAngle = cumulativeAngle
    }

    const chars = charsForTexts.value[textIndex]
    chars.forEach((char) => {
      if (style['text-transform']?.toLowerCase() === 'uppercase') {
        char = char.toUpperCase()
      }
      const charWidth = ctx.measureText(char).width
      // Calculate the angle for this specific character based on its measured width.
      const angle = ((charWidth * textSpacing) / radius) * (180 / Math.PI)

      // Position the character's center by using half of its angle.
      const rotation = cumulativeAngle + angle / 2

      styles.push({
        transform: `rotate(${rotation.toFixed(2)}deg) translateY(-${radius}px)${isFlipping ? ' rotate(180deg)' : ''}`,
      })

      // Add the full angle to the cumulative total for the next character.
      cumulativeAngle += angle
    })

    // Track the end angle for the year segment
    if (textIndex === 1) {
      yearEndAngle = cumulativeAngle
    }

    // Add spacing between segments.
    if (textIndex < texts.value.length - 1) cumulativeAngle += spacingBetweenSegments.value[textIndex] || 0
  })

  charStyles.value = styles

  // Create SVG arc path for year background
  if (yearStartAngle && yearEndAngle) {
    const arcRadius = radius
    const center = 56 // Center of the 112x112 SVG viewBox

    // Add padding to the arc (in degrees)
    const padding = -2
    const startAngle = yearStartAngle - padding - 42 // -42 matches the ringContainerStyle rotation
    const endAngle = yearEndAngle + padding - 42

    // Convert angles to radians
    const startRad = (startAngle * Math.PI) / 180
    const endRad = (endAngle * Math.PI) / 180

    // Calculate start and end points on the circle
    const startX = center + arcRadius * Math.sin(startRad)
    const startY = center - arcRadius * Math.cos(startRad)
    const endX = center + arcRadius * Math.sin(endRad)
    const endY = center - arcRadius * Math.cos(endRad)

    // Determine if arc is larger than 180 degrees
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0

    // Create the SVG path
    yearArcPath.value = `M ${startX} ${startY} A ${arcRadius} ${arcRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`
    yearArcVisible.value = true
  } else {
    yearArcVisible.value = false
  }

  ringContainerStyle.value = {
    color: getBeltTextColorHex(props.color),
    transform: `rotate(-42deg)`,
  }
})
</script>

<template>
  <div
    ref="containerRef"
    class="relative select-none w-[112px] h-[112px]"
    :style="{
      '--gradient-x': `${gradientXPercent}%`,
      '--gradient-y': `${gradientYPercent}%`,
      '--conic-angle': `${conicAngle}deg`,
    }"
  >
    <div class="relative w-28 h-28 overflow-visible rounded-full">
      <div class="absolute inset-0 overflow-visible">
        <svg
          width="112"
          height="112"
          viewBox="0 0 112 112"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="overflow-visible"
        >
          <defs>
            <filter id="shadow-ring" filterUnits="userSpaceOnUse">
              <feDropShadow dx="0" dy="0" stdDeviation="1" flood-color="black" flood-opacity="0.3" />
            </filter>
          </defs>
          <circle :style="{ filter: xShadow ? 'url(#shadow-ring)' : 'none' }" cx="56" cy="56" r="56" fill="white" />
          <circle
            :style="{ filter: xShadow ? 'url(#shadow-ring)' : 'none' }"
            cx="56"
            cy="56"
            r="56"
            :fill="badgeBackgroundProps.tintColor"
            :opacity="0.2"
          />
        </svg>
      </div>
      <!-- CSS-based holo effect overlay (shiny metallic style) -->
      <div
        class="holo-overlay"
        :style="{
          opacity: holoOpacity,
          '--badge-color': badgeBackgroundProps.holoBadgeColor,
          '--holo-color-opacity': holoOpacity * 0.6,
        }"
      />
    </div>
    <!-- Year arc background -->
    <svg
      v-if="yearArcVisible"
      class="absolute top-0 start-0 w-full h-full"
      width="112"
      height="112"
      viewBox="0 0 112 112"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path :d="yearArcPath" :stroke="getBeltYearArcColorHex(color)" stroke-width="10" stroke-linecap="round" />
    </svg>
    <!-- Belt border text -->
    <div
      v-if="texts"
      class="text-ring absolute top-0 start-0 w-full h-full"
      :style="ringContainerStyle"
      aria-hidden="true"
    >
      <!-- goodcheck-disable-next-line -->
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <OzIcon :size="12" name="crane" :style="{ transform: 'translateY(-46px)' }" />
      </div>

      <template v-for="(text, textIndex) in texts" :key="text">
        <div
          v-for="(char, charIndex) in charsForTexts[textIndex]"
          :key="charIndex"
          class="char"
          :style="[charStyles[charIndex + charsIndexOffsets[textIndex]], textStyles[textIndex]]"
          aria-hidden="true"
        >
          <div class="char-text">
            {{ char }}
          </div>
        </div>
      </template>
      <span class="sr-only">{{ TEXTS.join(' ') }}</span>
    </div>

    <!-- Belt inner item -->
    <div
      class="absolute top-0 start-0 bottom-0 end-0 w-full h-full flex items-center justify-center pointer-events-none"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
@import '@@/styles/outfit.scss';
.text-ring {
  font-family: 'Outfit';
  text-transform: uppercase;
  animation: rotation 20s infinite linear;
}

.char {
  position: absolute;
  top: 50%;
  left: 50%;
}

.char-text {
  font-family: inherit;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/**
 * Holographic overlay effect for badges
 *
 * Creates a multi-layered prismatic shine effect using stacked CSS gradients:
 * 1. Top layer: Iridescent color burst at cursor position (blues, yellows, greens, magentas)
 * 2. Second layer: Bright white spotlight following cursor for metallic highlight
 * 3. Third layer: Rotating rainbow rays (conic gradient) with dynamic angle based on cursor position
 * 4. Base layer: Subtle radial shine from center for ambient metallic glow
 *
 * The effect responds to mouse movement via CSS custom properties:
 * - --gradient-x, --gradient-y: Cursor position in percentage for radial gradients
 * - --conic-angle: Rotation angle for rainbow ray effect (smoothly animated)
 * - --badge-color: Belt-specific color with opacity for themed rainbow rays
 * - --holo-color-opacity: Dynamic opacity based on distance from center
 */
.holo-overlay {
  position: absolute;
  inset: 0;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  pointer-events: none;
  transition: all 0.3s ease-out;
  /* Performance optimizations for GPU acceleration */
  contain: layout style paint; /* Isolate rendering scope to prevent expensive repaints */
  transform: translateZ(0); /* Force GPU compositing layer */
  will-change: opacity; /* Hint browser for opacity changes */

  background:
    /* Layer 1: Iridescent color burst at cursor position
       Creates rainbow shimmer effect that follows the mouse
       Color sequence: cool blue → warm yellow → fresh green → magenta → cool lavender */
    radial-gradient(
      circle at var(--gradient-x) var(--gradient-y),
      rgba(200, 220, 255, 0.56) 0%,
      /* Cool blue at center */ rgba(255, 240, 200, 0.48) 10.7%,
      /* Warm yellow */ rgba(200, 255, 220, 0.4) 21.4%,
      /* Fresh green */ rgba(255, 200, 255, 0.32) 32.1%,
      /* Soft magenta */ rgba(220, 200, 255, 0.24) 42.8%,
      /* Cool lavender */ transparent 50% /* Fade to transparent */
    ),
    /* Layer 2: Bright white spotlight at cursor position
       Creates metallic highlight that emphasizes the holographic effect */
      radial-gradient(
        circle at var(--gradient-x) var(--gradient-y),
        rgba(255, 255, 255, 0.64) 0%,
        /* Bright core */ rgba(255, 255, 255, 0.6) 7.1%,
        /* Strong inner glow */ rgba(255, 255, 255, 0.48) 14.3%,
        /* Medium glow */ rgba(255, 255, 255, 0.36) 25%,
        /* Soft glow */ rgba(255, 255, 255, 0.16) 35.7%,
        /* Subtle glow */ transparent 50% /* Fade to transparent */
      ),
    /* Layer 3: Rotating rainbow rays (conic gradient)
       Creates radial streaks of color emanating from center
       Pattern: white rays (80% of each 30° segment) with colored accents (20% in middle)
       Angle is dynamically calculated based on cursor position relative to badge center */
      conic-gradient(
        from calc(var(--conic-angle) + 0deg) at 50% 50%,
        /* Segment 1: 0-60° (white-tinted-white) */ rgba(255, 255, 255, 0.9) 0deg,
        var(--badge-color) 30deg,
        /* Belt color tint */ rgba(255, 255, 255, 0.9) 60deg,
        /* Segment 2: 90-120° (white-yellow-white) */ var(--badge-color) 90deg,
        rgba(255, 212, 0, var(--holo-color-opacity)) 102deg,
        /* Gold yellow accent */ var(--badge-color) 108deg,
        rgba(255, 255, 255, 0.9) 120deg,
        /* Segment 3: 150-180° (white-green-white) */ var(--badge-color) 150deg,
        rgba(106, 255, 0, var(--holo-color-opacity)) 162deg,
        /* Lime green accent */ var(--badge-color) 168deg,
        rgba(255, 255, 255, 0.9) 180deg,
        /* Segment 4: 210-240° (white) */ var(--badge-color) 210deg,
        rgba(255, 255, 255, 0.9) 240deg,
        /* Segment 5: 270-300° (white-blue-white) */ var(--badge-color) 270deg,
        rgba(0, 157, 255, var(--holo-color-opacity)) 282deg,
        /* Cyan blue accent */ var(--badge-color) 288deg,
        rgba(255, 255, 255, 0.9) 300deg,
        /* Segment 6: 330-360° (white-red-white) */ var(--badge-color) 330deg,
        rgba(232, 15, 66, var(--holo-color-opacity)) 342deg,
        /* Vibrant red accent */ var(--badge-color) 348deg,
        rgba(255, 255, 255, 0.9) 360deg
      ),
    /* Layer 4: Subtle radial shine from center
       Provides ambient metallic glow independent of cursor position */
      radial-gradient(
        circle at 50% 50%,
        rgba(255, 255, 255, 0.9) 0%,
        /* Bright center */ rgba(255, 255, 255, 0.7) 15%,
        /* Strong inner */ rgba(255, 255, 255, 0.4) 30%,
        /* Medium */ rgba(255, 255, 255, 0.15) 50%,
        /* Subtle */ transparent 70% /* Fade out */
      );
}
</style>
